import React from "react";
import { useHistory, useParams } from "react-router";
import { useSurvey } from "../../../../../../common/hooks/api/useSurvey";
import { Api } from "../../../../../../common/service/Api";
import { UUIDString } from "../../../../../../common/service/server/Types";
import { OrganizationVendorSettingsDto } from "../../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import {
  LoadingPageWrapper,
  StandardPageWrapper,
} from "../../../../../components/layout/StandardPageWrapper";
import { PageHeader, VendorPageHeader } from "../../../../../components/layout/header/Header";
import {
  DataRecipientDisclosureSettings,
  finishedDisclosure,
  saveDisclosureUpdates,
  useDataRecipientDisclosureSettings,
} from "../../../../../components/organisms/data-recipients/DataRecipientDisclosureSettings";
import {
  useOrganizationVendors,
  usePrimaryOrganization,
  usePrimaryOrganizationId,
} from "../../../../../hooks";
import { useOrganizationVendorDetails } from "../../../../../hooks/useOrganizationVendors";
import { RouteHelpers } from "../../../../../root/routes";
import { isThirdPartyRecipient } from "../../../../../util/vendors";
import { ProgressFooter, ProgressFooterActions } from "../../../../get-compliant/ProgressFooter";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../../../get-compliant/data-recipients/shared";
import { NetworkRequest } from "../../../../../../common/models/NetworkRequest";

type Params = {
  dataRecipientId: UUIDString;
};

export type UpdateVendorArgs = {
  vendorId: string;
  vendorSettings: OrganizationVendorSettingsDto;
};

export const ClassificationForDataRecipientPage = () => {
  // Variables
  const history = useHistory();
  const organizationId = usePrimaryOrganizationId();
  const [org] = usePrimaryOrganization();
  const { dataRecipientId } = useParams<Params>();

  // State
  const [orgVendors, orgVendorsRequest] = useOrganizationVendors(organizationId, undefined, true);

  // Requests
  const [dataRecipient, dataRecipientRequest, dataRecipientRefresh] = useOrganizationVendorDetails(
    org.id,
    dataRecipientId,
  );

  const {
    answers,
    request: surveyQuestionsRequest,
    mutators: { patchAnswer: patchSellingAndSharingAnswer },
  } = useSurvey(organizationId, SELLING_AND_SHARING_SURVEY_NAME);

  const isStorageLocation = dataRecipient?.dataRecipientType == "storage_location";

  dataRecipientRefresh();
  const {
    state: disclosureState,
    meta: disclosureMeta,
    updateState: disclosureUpdate,
  } = useDataRecipientDisclosureSettings(
    dataRecipient,
    answers,
    org,
    false,
    orgVendors,
    dataRecipient?.category,
  );

  const updateVendor = async (v: UpdateVendorArgs) => {
    const { vendorId, vendorSettings } = v;
    await Api.organizationVendor.addOrUpdateVendor(org.id, vendorId, vendorSettings);
  };

  const vendorName = dataRecipient?.name;

  const descLine = [dataRecipient?.category, dataRecipient?.url, dataRecipient?.email]
    .filter((x) => x)
    .join(" · ");
  const desc = (
    <>
      <p className="text-muted text-t1 my-0">{descLine}</p>
      {dataRecipient?.dpaUrl && (
        <p className="text-t1 my-0">
          <a href={dataRecipient?.dpaUrl} target="_blank" rel="noopener noreferrer">
            Data Processing Agreement (DPA)
          </a>
        </p>
      )}
    </>
  );

  const finished =
    isStorageLocation || finishedDisclosure(disclosureState, disclosureMeta, org, false);

  const onDone = async () => {
    if (!isStorageLocation) {
      await saveDisclosureUpdates(
        org,
        dataRecipient,
        answers,
        disclosureState,
        updateVendor,
        patchSellingAndSharingAnswer,
      );
    }

    history.push(RouteHelpers.dataMap.dataRecipient.detail(dataRecipientId));
  };

  if (!dataRecipient) {
    return null;
  }

  // Page Load
  if (!NetworkRequest.isFinished(dataRecipientRequest)) {
    return <LoadingPageWrapper />;
  }

  return (
    <StandardPageWrapper
      requests={[surveyQuestionsRequest, dataRecipientRequest, orgVendorsRequest]}
      footer={
        <ProgressFooter
          actions={
            <ProgressFooterActions
              nextOnClick={onDone}
              nextDisabled={!finished}
              nextDisabledTooltip={
                org.featureGdpr
                  ? "Choose at least one region and answer each question."
                  : "Answer each question."
              }
              nextContent="Done"
              prevContent="Back"
              prevUrl={RouteHelpers.dataMap.dataRecipient.workflow.disclosure(dataRecipientId)}
            />
          }
        />
      }
    >
      {Boolean(dataRecipient) && (
        <VendorPageHeader
          vendor={dataRecipient}
          titleContent={vendorName}
          descriptionContent={desc}
        />
      )}

      {!dataRecipient && (
        <PageHeader
          titleContent={
            isThirdPartyRecipient(dataRecipient) ? "Add Third Party Recipient" : "Add Vendor"
          }
        />
      )}

      {!isStorageLocation && (
        <DataRecipientDisclosureSettings
          org={org}
          vendor={dataRecipient}
          isThirdParty={isThirdPartyRecipient(dataRecipient)}
          disclosureState={disclosureState}
          disclosureMeta={disclosureMeta}
          updateDisclosureState={disclosureUpdate}
        />
      )}
    </StandardPageWrapper>
  );
};
